import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Save, BookOpen } from "lucide-react";

export function CourseContentTab() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [lessons, setLessons] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/courses/admin/lessons`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setLessons(r.data.lessons || []);
        const d = {};
        (r.data.lessons || []).forEach((l) => { d[l.lesson_id] = { concept: l.concept, example: l.example }; });
        setDrafts(d);
      }).catch((e) => { console.error("admin lessons load failed", e?.response?.status); toast.error("Couldn't load lessons"); });
  }, [token]);

  const save = async (id) => {
    setSavingId(id);
    try {
      await axios.put(`${API}/courses/lesson/${id}/content`, drafts[id], { headers });
      toast.success("Lesson content saved");
    } catch (e) { toast.error("Save failed"); }
    setSavingId(null);
  };

  const grouped = lessons.reduce((acc, l) => {
    (acc[l.course] = acc[l.course] || []).push(l);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-course-content">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold">Course Content</h2>
        <span className="text-sm text-muted-foreground">Edit each lesson's concept & worked example</span>
      </div>
      {Object.entries(grouped).map(([course, items]) => (
        <div key={course} className="space-y-3">
          <h3 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">{course}</h3>
          {items.map((l) => (
            <div key={l.lesson_id} className="border border-border rounded-xl p-4 space-y-2" data-testid={`admin-lesson-${l.lesson_id}`}>
              <p className="font-semibold text-foreground">{l.level} · {l.title}</p>
              <div>
                <label className="text-xs text-muted-foreground">Concept</label>
                <RichTextEditor value={drafts[l.lesson_id]?.concept || ""} minHeight={90}
                  onChange={(html) => setDrafts((d) => ({ ...d, [l.lesson_id]: { ...d[l.lesson_id], concept: html } }))}
                  testId={`concept-${l.lesson_id}`} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Worked Example</label>
                <RichTextEditor value={drafts[l.lesson_id]?.example || ""} minHeight={70}
                  onChange={(html) => setDrafts((d) => ({ ...d, [l.lesson_id]: { ...d[l.lesson_id], example: html } }))}
                  testId={`example-${l.lesson_id}`} />
              </div>
              <Button size="sm" onClick={() => save(l.lesson_id)} disabled={savingId === l.lesson_id} data-testid={`save-${l.lesson_id}`}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
